import dayjs from 'dayjs';
import { APPLICATION_STATUS_TYPE } from '../statusConstants';
import { ROLES_MODULE } from '../../components';

export type FormController = {
  formModificationMode: FormModificationMode;
};

export enum FormModificationMode {
  ENABLE = 'ENABLE', // user can edit all fields, including adding, deleting forms.
  DISABLE = 'DISABLE', // user can only view the form, no edit permission.
  MODIFIED_ONLY = 'MODIFIED_ONLY', // user can only edit some specific fields, the rest are disabled.
  CONDITION_MEET = 'CONDITION_MEET', // user can only edit the form for conditional offer.
}

type EnableAction = {
  programmeChoice: boolean;
  academicQualification: FormController;
  academicReferee: boolean;
  languageQualification: FormController;
  publication: FormController;
  otherTest: FormController;
  additionalDocuments: FormController;
  proofOfDocument: FormController;
  distinctiveScholarship: FormController;
  commonForms: boolean;
  enableDelete: boolean;
  enableSubmit?: boolean;
  enableWithdraw?: boolean;
};

export enum SimplifyRoles {
  APPLICANT = 'APPLICANT',
  SUPERVISOR = 'SUPERVISOR',
  DORPGS = 'DO_RPG_S',
  DRC = 'DRC',
  FRC = 'FRC',
  GS = 'GS',
  HOD = 'HOD',
  VPRI = 'VPRI',
  PANEL = 'PANEL',
  INTERVIEW_PANEL = 'INTERVIEW_PANEL',
  AR = 'AR',
}

export const getSimplifyRole = (role: ROLES_MODULE): SimplifyRoles => {
  switch (role) {
    case ROLES_MODULE.SUPERVISOR:
      return SimplifyRoles.SUPERVISOR;
    case ROLES_MODULE.RIGHT_DO_RPG_S:
    case ROLES_MODULE.RIGHT_DO_RPG_S_GO:
    case ROLES_MODULE.RIGHT_DO_RPG_S_SEC:
      return SimplifyRoles.DORPGS;
    case ROLES_MODULE.RIGHT_DRC_CHAIR:
    case ROLES_MODULE.RIGHT_DRC_SEC:
    case ROLES_MODULE.RIGHT_DRC:
    case ROLES_MODULE.RIGHT_DRC_GO:
      return SimplifyRoles.DRC;
    case ROLES_MODULE.RIGHT_FRC:
    case ROLES_MODULE.RIGHT_FRC_CHAIR:
    case ROLES_MODULE.RIGHT_FRC_GO:
    case ROLES_MODULE.RIGHT_FRC_SEC:
      return SimplifyRoles.FRC;
    case ROLES_MODULE.RIGHT_GS:
    case ROLES_MODULE.RIGHT_GSB:
    case ROLES_MODULE.RIGHT_GSB_CHAIR:
    case ROLES_MODULE.RIGHT_GSB_GO:
    case ROLES_MODULE.RIGHT_GSB_SEC:
      return SimplifyRoles.GS;
    case ROLES_MODULE.RIGHT_HOD_DOS:
    case ROLES_MODULE.RIGHT_HOD_DOS_SEC:
      return SimplifyRoles.HOD;
    case ROLES_MODULE.RIGHT_VPRI:
      return SimplifyRoles.VPRI;
    case ROLES_MODULE.RIGHT_PANEL:
      return SimplifyRoles.PANEL;
    case ROLES_MODULE.RIGHT_INTERVIEW_PANEL:
      return SimplifyRoles.INTERVIEW_PANEL;
    case ROLES_MODULE.RIGHT_AR:
      return SimplifyRoles.AR;
    default:
      return SimplifyRoles.APPLICANT;
  }
};

export const enableEditApplication = (
  role: ROLES_MODULE,
  status: APPLICATION_STATUS_TYPE,
  commonControl: {
    entryYear: string;
    isLanguageEditable: boolean;
    isAcademicQualificationEditable: boolean;
    isOtherTestEditable: boolean;
    isAdditionalDocumentEditable: boolean;
    isPreview: boolean;
    isDistinctiveApplicant: boolean;
  },
): EnableAction => {
  const disableFormController: FormController = {
    formModificationMode: FormModificationMode.DISABLE,
  };

  const enableFormController: FormController = {
    formModificationMode: FormModificationMode.ENABLE,
  };

  // currently only used for UPAF
  const modifiedOnlyFormController: FormController = {
    formModificationMode: FormModificationMode.MODIFIED_ONLY,
  };

  const conditionalFormController: FormController = {
    formModificationMode: FormModificationMode.CONDITION_MEET,
  };

  const disabledAllForms: EnableAction = {
    programmeChoice: false,
    academicQualification: disableFormController,
    academicReferee: false,
    languageQualification: disableFormController,
    otherTest: disableFormController,
    additionalDocuments: disableFormController,
    publication: disableFormController,
    proofOfDocument: disableFormController,
    distinctiveScholarship: disableFormController,
    commonForms: false,
    enableDelete: false,
    enableSubmit: false,
    enableWithdraw: false,
  };

  if (role === undefined || status === undefined) {
    return disabledAllForms;
  }

  const simplifyRole = getSimplifyRole(role);
  const {
    entryYear,
    isLanguageEditable,
    isAcademicQualificationEditable,
    isOtherTestEditable,
    isAdditionalDocumentEditable,
    isPreview,
    isDistinctiveApplicant,
  } = commonControl;

  // Updated at 06/03/2025, for RPG-1799, opening the second round of scholarship application for 2025-02
  // For 2025-01, returned status, they can edit anytime
  // For 2o25-02, created status, they can edit in opening date; returned status, they can edit anytime
  // Updated at 01/04/2025, for RPG-1861, returned application can only modify and remove upaf, but add upaf is NOT permitted
  // Staff can add, update, remove upaf during the open date and nomination date

  const openEntryYear =
    process.env.DICTINCYIVE_SCHOLARSHIP_ENTRY_YEAR ?? '2025-02';
  const secondOpenDate =
    process.env.DICTINCYIVE_SCHOLARSHIP_STARTTIME ?? '2025-03-09T16:00:00.000Z';
  const secondCloseDate =
    process.env.DICTINCYIVE_SCHOLARSHIP_APPLICATION_ENDTIME ??
    '2025-04-01T15:59:59.000Z';
  const nominationEndDate =
    process.env.DICTINCYIVE_SCHOLARSHIP_NOMINATION_ENDTIME ??
    '2025-04-28T15:59:59.000Z';

  const openEditEntryYear = entryYear === openEntryYear;

  const upafApplicantEditable: boolean =
    dayjs().isAfter(secondOpenDate) &&
    dayjs().isBefore(secondCloseDate) &&
    openEditEntryYear;

  const upafStaffEditable: boolean =
    dayjs().isAfter(secondOpenDate) &&
    dayjs().isBefore(nominationEndDate) &&
    openEditEntryYear;

  const isApplicant = simplifyRole === SimplifyRoles.APPLICANT;

  const applicantFormsSubmit: EnableAction = {
    programmeChoice: false,
    academicQualification: disableFormController,
    academicReferee: false,
    languageQualification: disableFormController,
    publication: disableFormController,
    otherTest: disableFormController,
    additionalDocuments: disableFormController,
    proofOfDocument: disableFormController,
    distinctiveScholarship: disableFormController,
    commonForms: false,
    enableDelete: false,
    enableSubmit: false,
    enableWithdraw: true,
  };

  const applicantFormsAssessment: EnableAction = {
    programmeChoice: false,
    academicQualification: disableFormController,
    academicReferee: false,
    languageQualification: disableFormController,
    publication: disableFormController,
    otherTest: disableFormController,
    additionalDocuments: disableFormController,
    proofOfDocument: disableFormController,
    distinctiveScholarship: disableFormController,
    commonForms: false,
    enableDelete: false,
    enableSubmit: false,
    enableWithdraw: true,
  };

  const applicantFormsConditionalOffer: EnableAction = {
    programmeChoice: false,
    academicQualification: isAcademicQualificationEditable
      ? conditionalFormController
      : disableFormController,
    academicReferee: false,
    languageQualification: isLanguageEditable
      ? conditionalFormController
      : disableFormController,
    otherTest: isOtherTestEditable
      ? conditionalFormController
      : disableFormController,
    additionalDocuments: isAdditionalDocumentEditable
      ? conditionalFormController
      : disableFormController,
    publication: disableFormController,
    proofOfDocument: disableFormController,
    distinctiveScholarship: disableFormController,
    commonForms: false,
    enableDelete: false, // in condition meet, the applicant cannot delete any form
    enableSubmit: false,
    enableWithdraw: true,
  };

  const commonStaffViewForms: EnableAction = {
    programmeChoice: false,
    academicQualification: enableFormController,
    academicReferee: false, // all staff can only edit the referee form in CREATED status
    languageQualification: enableFormController,
    publication: enableFormController,
    otherTest: enableFormController,
    additionalDocuments: enableFormController,
    proofOfDocument: disableFormController, // only AR & GS member are able to edit
    commonForms: true,
    enableDelete: false, // only GS members are able to delete
    distinctiveScholarship: disableFormController,
  };

  const enableEditForms: EnableAction = {
    programmeChoice: true,
    academicQualification: enableFormController,
    academicReferee: false,
    languageQualification: enableFormController,
    publication: enableFormController,
    otherTest: enableFormController,
    additionalDocuments: enableFormController,
    proofOfDocument: enableFormController,
    distinctiveScholarship: upafStaffEditable
      ? enableFormController
      : disableFormController,
    enableDelete: true,
    commonForms: true,
  };

  const enableDorpgEditForms: EnableAction = {
    ...commonStaffViewForms,
    distinctiveScholarship: upafStaffEditable
      ? enableFormController
      : disableFormController,
  };

  const enableAREditForms: EnableAction = {
    ...enableEditForms,
    enableDelete: false,
    programmeChoice: false,
    distinctiveScholarship: disableFormController,
  };

  const commonStaffRoles: SimplifyRoles[] = [
    SimplifyRoles.SUPERVISOR,
    SimplifyRoles.DORPGS,
    SimplifyRoles.DRC,
    SimplifyRoles.FRC,
    SimplifyRoles.HOD,
    SimplifyRoles.VPRI,
    SimplifyRoles.PANEL,
    SimplifyRoles.AR,
  ];
  // for situation that staff view as applicant
  // should hide submit button, and keep save function to update application forms
  // even though the status of application indicates that the applicant can not edit any more
  // the academic staff still get the right to edit every form
  const enableEdit: Partial<
    Record<SimplifyRoles, Record<APPLICATION_STATUS_TYPE, EnableAction>>
  > = {
    // Limits of authority for applicant
    [SimplifyRoles.APPLICANT]: {
      [APPLICATION_STATUS_TYPE.CREATED]: {
        programmeChoice: !isPreview,
        academicQualification: !isPreview
          ? enableFormController
          : disableFormController,
        academicReferee: !isPreview,
        languageQualification: !isPreview
          ? enableFormController
          : disableFormController,
        otherTest: !isPreview ? enableFormController : disableFormController,
        additionalDocuments: !isPreview
          ? enableFormController
          : disableFormController,
        publication: !isPreview ? enableFormController : disableFormController,
        commonForms: !isPreview,
        enableDelete: !isPreview,
        distinctiveScholarship: isPreview
          ? disableFormController
          : upafApplicantEditable
            ? enableFormController
            : disableFormController,
        proofOfDocument: disableFormController,
        enableSubmit: true,
        enableWithdraw: true,
      },
      [APPLICATION_STATUS_TYPE.APPLICATION_FEE_PENDING]: applicantFormsSubmit,
      [APPLICATION_STATUS_TYPE.PARTNER_NOMINATION_PENDING]:
        applicantFormsSubmit,
      [APPLICATION_STATUS_TYPE.SUBMITTED]: applicantFormsSubmit,
      [APPLICATION_STATUS_TYPE.UNDER_SUPERVISOR]: applicantFormsSubmit,
      [APPLICATION_STATUS_TYPE.SUPERVISOR_NOMINATED]: applicantFormsSubmit,
      [APPLICATION_STATUS_TYPE.IN_NOMINATION_POOL]: applicantFormsSubmit,
      [APPLICATION_STATUS_TYPE.UNDER_DORPGS]: applicantFormsSubmit,

      // The following statuses are not editable by the applicant
      [APPLICATION_STATUS_TYPE.MODIFICATION_FROZEN]: applicantFormsAssessment,
      [APPLICATION_STATUS_TYPE.UNDER_DSRC]: applicantFormsAssessment,
      [APPLICATION_STATUS_TYPE.DSRC_ASSESSED]: applicantFormsAssessment,
      [APPLICATION_STATUS_TYPE.UNDER_HOD_DOS]: applicantFormsAssessment,
      [APPLICATION_STATUS_TYPE.UNDER_FRCSB]: applicantFormsAssessment,
      [APPLICATION_STATUS_TYPE.FRCSB_ASSESSED]: applicantFormsAssessment,
      [APPLICATION_STATUS_TYPE.UNDER_GS]: applicantFormsAssessment,
      [APPLICATION_STATUS_TYPE.GS_ASSESSED]: applicantFormsAssessment,
      [APPLICATION_STATUS_TYPE.PANEL_NOMINATED]: applicantFormsAssessment,
      [APPLICATION_STATUS_TYPE.UNDER_PANEL]: applicantFormsAssessment,
      [APPLICATION_STATUS_TYPE.UNDER_INTERVIEW_PANEL]: applicantFormsAssessment,

      [APPLICATION_STATUS_TYPE.OFFERED]: applicantFormsAssessment,
      [APPLICATION_STATUS_TYPE.UNSUCCESSFUL]: applicantFormsAssessment,
      [APPLICATION_STATUS_TYPE.OFFER_PROVISIONING]:
        applicantFormsConditionalOffer,
      [APPLICATION_STATUS_TYPE.FAILED_PROVISIONING]:
        applicantFormsConditionalOffer,
      [APPLICATION_STATUS_TYPE.REGISTERED]: applicantFormsConditionalOffer,
      [APPLICATION_STATUS_TYPE.REGISTRATION_FEE_PENDING]:
        applicantFormsConditionalOffer,
      [APPLICATION_STATUS_TYPE.REGISTRATION_FEE_SETTLED]:
        applicantFormsConditionalOffer,
      [APPLICATION_STATUS_TYPE.UNDER_REGISTRATION]:
        applicantFormsConditionalOffer,
      [APPLICATION_STATUS_TYPE.RETURNED]: {
        programmeChoice: false,
        academicQualification: !isPreview
          ? enableFormController
          : disableFormController,
        academicReferee: false,
        languageQualification: !isPreview
          ? enableFormController
          : disableFormController,
        otherTest: !isPreview ? enableFormController : disableFormController,
        additionalDocuments: !isPreview
          ? enableFormController
          : disableFormController,
        publication: !isPreview ? enableFormController : disableFormController,
        // both common staff members and applicants are prohibited from deleting any forms within a returned application
        enableDelete: false,
        commonForms: !isPreview,
        proofOfDocument: disableFormController,
        distinctiveScholarship: modifiedOnlyFormController,
        enableSubmit: true,
        enableWithdraw: true,
      },
      [APPLICATION_STATUS_TYPE.WITHDRAWN]: disabledAllForms,
      [APPLICATION_STATUS_TYPE.OVERDUE]: disabledAllForms,
      [APPLICATION_STATUS_TYPE.DECLINED]: disabledAllForms,
      [APPLICATION_STATUS_TYPE.LAPSED]: disabledAllForms,
      [APPLICATION_STATUS_TYPE.CONDITION_MEET_LAPSED]: disabledAllForms,
    },
    // Limits of authority for common staff except GS

    ...commonStaffRoles.reduce<
      Partial<
        Record<SimplifyRoles, Record<APPLICATION_STATUS_TYPE, EnableAction>>
      >
    >((acc, role: SimplifyRoles) => {
      acc[role] = {
        [APPLICATION_STATUS_TYPE.CREATED]: {
          ...commonStaffViewForms,
          academicReferee: true,
        },
        [APPLICATION_STATUS_TYPE.APPLICATION_FEE_PENDING]: commonStaffViewForms,
        [APPLICATION_STATUS_TYPE.PARTNER_NOMINATION_PENDING]:
          commonStaffViewForms,
        [APPLICATION_STATUS_TYPE.SUBMITTED]: commonStaffViewForms,
        [APPLICATION_STATUS_TYPE.UNDER_SUPERVISOR]: commonStaffViewForms,
        [APPLICATION_STATUS_TYPE.SUPERVISOR_NOMINATED]: commonStaffViewForms,
        [APPLICATION_STATUS_TYPE.IN_NOMINATION_POOL]: commonStaffViewForms,

        [APPLICATION_STATUS_TYPE.MODIFICATION_FROZEN]: commonStaffViewForms,
        [APPLICATION_STATUS_TYPE.UNDER_DORPGS]: commonStaffViewForms,
        [APPLICATION_STATUS_TYPE.UNDER_DSRC]: commonStaffViewForms,
        [APPLICATION_STATUS_TYPE.DSRC_ASSESSED]: commonStaffViewForms,
        [APPLICATION_STATUS_TYPE.UNDER_HOD_DOS]: commonStaffViewForms,
        [APPLICATION_STATUS_TYPE.UNDER_FRCSB]: commonStaffViewForms,
        [APPLICATION_STATUS_TYPE.FRCSB_ASSESSED]: commonStaffViewForms,
        [APPLICATION_STATUS_TYPE.UNDER_GS]: commonStaffViewForms,
        [APPLICATION_STATUS_TYPE.GS_ASSESSED]: commonStaffViewForms,
        [APPLICATION_STATUS_TYPE.PANEL_NOMINATED]: commonStaffViewForms,
        [APPLICATION_STATUS_TYPE.UNDER_INTERVIEW_PANEL]: commonStaffViewForms,
        [APPLICATION_STATUS_TYPE.OFFERED]: commonStaffViewForms,
        [APPLICATION_STATUS_TYPE.UNSUCCESSFUL]: commonStaffViewForms,
        [APPLICATION_STATUS_TYPE.OFFER_PROVISIONING]: commonStaffViewForms,

        [APPLICATION_STATUS_TYPE.FAILED_PROVISIONING]: commonStaffViewForms,
        [APPLICATION_STATUS_TYPE.CONDITION_MEET_LAPSED]: commonStaffViewForms,

        [APPLICATION_STATUS_TYPE.REGISTERED]: commonStaffViewForms,
        [APPLICATION_STATUS_TYPE.REGISTRATION_FEE_PENDING]:
          commonStaffViewForms,
        [APPLICATION_STATUS_TYPE.REGISTRATION_FEE_SETTLED]:
          commonStaffViewForms,
        [APPLICATION_STATUS_TYPE.UNDER_REGISTRATION]: commonStaffViewForms,
        [APPLICATION_STATUS_TYPE.RETURNED]: {
          ...commonStaffViewForms,
          distinctiveScholarship: isDistinctiveApplicant
            ? modifiedOnlyFormController
            : disableFormController,
        },
        [APPLICATION_STATUS_TYPE.UNDER_PANEL]: commonStaffViewForms,
        [APPLICATION_STATUS_TYPE.WITHDRAWN]: commonStaffViewForms,
        [APPLICATION_STATUS_TYPE.OVERDUE]: commonStaffViewForms,
        [APPLICATION_STATUS_TYPE.DECLINED]: commonStaffViewForms,
        [APPLICATION_STATUS_TYPE.LAPSED]: commonStaffViewForms,
      };
      return acc;
    }, {}),

    // Limits of authority for DoRPG
    [SimplifyRoles.DORPGS]: {
      [APPLICATION_STATUS_TYPE.CREATED]: {
        ...enableDorpgEditForms,
        academicReferee: true,
      },
      [APPLICATION_STATUS_TYPE.APPLICATION_FEE_PENDING]: enableDorpgEditForms,
      [APPLICATION_STATUS_TYPE.PARTNER_NOMINATION_PENDING]:
        enableDorpgEditForms,
      [APPLICATION_STATUS_TYPE.SUBMITTED]: enableDorpgEditForms,
      [APPLICATION_STATUS_TYPE.UNDER_SUPERVISOR]: enableDorpgEditForms,
      [APPLICATION_STATUS_TYPE.SUPERVISOR_NOMINATED]: enableDorpgEditForms,
      [APPLICATION_STATUS_TYPE.IN_NOMINATION_POOL]: enableDorpgEditForms,

      [APPLICATION_STATUS_TYPE.MODIFICATION_FROZEN]: enableDorpgEditForms,
      [APPLICATION_STATUS_TYPE.UNDER_DORPGS]: enableDorpgEditForms,
      [APPLICATION_STATUS_TYPE.UNDER_DSRC]: enableDorpgEditForms,
      [APPLICATION_STATUS_TYPE.DSRC_ASSESSED]: enableDorpgEditForms,
      [APPLICATION_STATUS_TYPE.UNDER_HOD_DOS]: enableDorpgEditForms,
      [APPLICATION_STATUS_TYPE.UNDER_FRCSB]: enableDorpgEditForms,
      [APPLICATION_STATUS_TYPE.FRCSB_ASSESSED]: enableDorpgEditForms,
      [APPLICATION_STATUS_TYPE.UNDER_GS]: enableDorpgEditForms,
      [APPLICATION_STATUS_TYPE.GS_ASSESSED]: enableDorpgEditForms,
      [APPLICATION_STATUS_TYPE.PANEL_NOMINATED]: enableDorpgEditForms,
      [APPLICATION_STATUS_TYPE.UNDER_INTERVIEW_PANEL]: enableDorpgEditForms,
      [APPLICATION_STATUS_TYPE.OFFERED]: enableDorpgEditForms,
      [APPLICATION_STATUS_TYPE.UNSUCCESSFUL]: enableDorpgEditForms,
      [APPLICATION_STATUS_TYPE.OFFER_PROVISIONING]: enableDorpgEditForms,

      [APPLICATION_STATUS_TYPE.FAILED_PROVISIONING]: enableDorpgEditForms,
      [APPLICATION_STATUS_TYPE.CONDITION_MEET_LAPSED]: enableDorpgEditForms,

      [APPLICATION_STATUS_TYPE.REGISTERED]: enableDorpgEditForms,
      [APPLICATION_STATUS_TYPE.REGISTRATION_FEE_PENDING]: enableDorpgEditForms,
      [APPLICATION_STATUS_TYPE.REGISTRATION_FEE_SETTLED]: enableDorpgEditForms,
      [APPLICATION_STATUS_TYPE.UNDER_REGISTRATION]: enableDorpgEditForms,
      [APPLICATION_STATUS_TYPE.RETURNED]: enableDorpgEditForms,
      [APPLICATION_STATUS_TYPE.UNDER_PANEL]: enableDorpgEditForms,
      [APPLICATION_STATUS_TYPE.WITHDRAWN]: enableDorpgEditForms,
      [APPLICATION_STATUS_TYPE.OVERDUE]: enableDorpgEditForms,
      [APPLICATION_STATUS_TYPE.DECLINED]: enableDorpgEditForms,
      [APPLICATION_STATUS_TYPE.LAPSED]: enableDorpgEditForms,
    },

    // Limits of authority for AR
    [SimplifyRoles.AR]: {
      [APPLICATION_STATUS_TYPE.CREATED]: {
        ...enableAREditForms,
        academicReferee: true,
      },
      [APPLICATION_STATUS_TYPE.APPLICATION_FEE_PENDING]: enableAREditForms,
      [APPLICATION_STATUS_TYPE.PARTNER_NOMINATION_PENDING]: enableAREditForms,
      [APPLICATION_STATUS_TYPE.SUBMITTED]: enableAREditForms,
      [APPLICATION_STATUS_TYPE.UNDER_SUPERVISOR]: enableAREditForms,
      [APPLICATION_STATUS_TYPE.SUPERVISOR_NOMINATED]: enableAREditForms,
      [APPLICATION_STATUS_TYPE.IN_NOMINATION_POOL]: enableAREditForms,
      [APPLICATION_STATUS_TYPE.MODIFICATION_FROZEN]: enableAREditForms,
      [APPLICATION_STATUS_TYPE.UNDER_DORPGS]: enableAREditForms,
      [APPLICATION_STATUS_TYPE.UNDER_DSRC]: enableAREditForms,
      [APPLICATION_STATUS_TYPE.DSRC_ASSESSED]: enableAREditForms,
      [APPLICATION_STATUS_TYPE.UNDER_HOD_DOS]: enableAREditForms,
      [APPLICATION_STATUS_TYPE.UNDER_FRCSB]: enableAREditForms,
      [APPLICATION_STATUS_TYPE.FRCSB_ASSESSED]: enableAREditForms,
      [APPLICATION_STATUS_TYPE.UNDER_GS]: enableAREditForms,
      [APPLICATION_STATUS_TYPE.GS_ASSESSED]: enableAREditForms,
      [APPLICATION_STATUS_TYPE.UNDER_INTERVIEW_PANEL]: enableAREditForms,
      [APPLICATION_STATUS_TYPE.PANEL_NOMINATED]: enableAREditForms,
      [APPLICATION_STATUS_TYPE.OFFERED]: enableAREditForms,
      [APPLICATION_STATUS_TYPE.UNSUCCESSFUL]: enableAREditForms,
      [APPLICATION_STATUS_TYPE.OFFER_PROVISIONING]: enableAREditForms,

      [APPLICATION_STATUS_TYPE.FAILED_PROVISIONING]: commonStaffViewForms,
      [APPLICATION_STATUS_TYPE.CONDITION_MEET_LAPSED]: commonStaffViewForms,

      [APPLICATION_STATUS_TYPE.REGISTERED]: enableAREditForms,
      [APPLICATION_STATUS_TYPE.REGISTRATION_FEE_PENDING]: enableAREditForms,
      [APPLICATION_STATUS_TYPE.REGISTRATION_FEE_SETTLED]: enableAREditForms,
      [APPLICATION_STATUS_TYPE.UNDER_REGISTRATION]: enableAREditForms,
      [APPLICATION_STATUS_TYPE.RETURNED]: enableAREditForms,
      [APPLICATION_STATUS_TYPE.UNDER_PANEL]: enableAREditForms,
      [APPLICATION_STATUS_TYPE.WITHDRAWN]: enableAREditForms,
      [APPLICATION_STATUS_TYPE.OVERDUE]: enableAREditForms,
      [APPLICATION_STATUS_TYPE.DECLINED]: enableAREditForms,
      [APPLICATION_STATUS_TYPE.LAPSED]: enableAREditForms,
    },

    // Limits of authority for GS
    [SimplifyRoles.GS]: {
      [APPLICATION_STATUS_TYPE.CREATED]: {
        ...enableEditForms,
        academicReferee: true,
      },
      [APPLICATION_STATUS_TYPE.APPLICATION_FEE_PENDING]: enableEditForms,
      [APPLICATION_STATUS_TYPE.PARTNER_NOMINATION_PENDING]: enableEditForms,
      [APPLICATION_STATUS_TYPE.SUBMITTED]: enableEditForms,
      [APPLICATION_STATUS_TYPE.UNDER_SUPERVISOR]: enableEditForms,
      [APPLICATION_STATUS_TYPE.SUPERVISOR_NOMINATED]: enableEditForms,
      [APPLICATION_STATUS_TYPE.IN_NOMINATION_POOL]: enableEditForms,
      [APPLICATION_STATUS_TYPE.MODIFICATION_FROZEN]: enableEditForms,
      [APPLICATION_STATUS_TYPE.UNDER_DORPGS]: enableEditForms,
      [APPLICATION_STATUS_TYPE.UNDER_DSRC]: enableEditForms,
      [APPLICATION_STATUS_TYPE.DSRC_ASSESSED]: enableEditForms,
      [APPLICATION_STATUS_TYPE.UNDER_HOD_DOS]: enableEditForms,
      [APPLICATION_STATUS_TYPE.UNDER_FRCSB]: enableEditForms,
      [APPLICATION_STATUS_TYPE.FRCSB_ASSESSED]: enableEditForms,
      [APPLICATION_STATUS_TYPE.UNDER_GS]: enableEditForms,
      [APPLICATION_STATUS_TYPE.GS_ASSESSED]: enableEditForms,
      [APPLICATION_STATUS_TYPE.UNDER_INTERVIEW_PANEL]: enableEditForms,
      [APPLICATION_STATUS_TYPE.PANEL_NOMINATED]: enableEditForms,
      [APPLICATION_STATUS_TYPE.OFFERED]: enableEditForms,
      [APPLICATION_STATUS_TYPE.UNSUCCESSFUL]: enableEditForms,
      [APPLICATION_STATUS_TYPE.OFFER_PROVISIONING]: enableEditForms,

      [APPLICATION_STATUS_TYPE.FAILED_PROVISIONING]: commonStaffViewForms,
      [APPLICATION_STATUS_TYPE.CONDITION_MEET_LAPSED]: commonStaffViewForms,

      [APPLICATION_STATUS_TYPE.REGISTERED]: enableEditForms,
      [APPLICATION_STATUS_TYPE.REGISTRATION_FEE_PENDING]: enableEditForms,
      [APPLICATION_STATUS_TYPE.REGISTRATION_FEE_SETTLED]: enableEditForms,
      [APPLICATION_STATUS_TYPE.UNDER_REGISTRATION]: enableEditForms,
      [APPLICATION_STATUS_TYPE.RETURNED]: enableEditForms,
      [APPLICATION_STATUS_TYPE.UNDER_PANEL]: enableEditForms,
      [APPLICATION_STATUS_TYPE.WITHDRAWN]: enableEditForms,
      [APPLICATION_STATUS_TYPE.OVERDUE]: enableEditForms,
      [APPLICATION_STATUS_TYPE.DECLINED]: enableEditForms,
      [APPLICATION_STATUS_TYPE.LAPSED]: enableEditForms,
    },
  };

  return isApplicant
    ? (enableEdit?.[simplifyRole]?.[status] ?? disabledAllForms)
    : {
        ...(enableEdit?.[simplifyRole]?.[status] ?? disabledAllForms),
        enableSubmit: false,
        enableWithdraw: false,
      };
};
